import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Tabs,
  Banner,
  Button,
  Text,
  Spinner,
  Frame,
  Toast
} from '@shopify/polaris';
import ProductSelector from './components/ProductSelector';
import LabelDesigner from './components/LabelDesigner';
import PrintQueue from './components/PrintQueue';
import SubscriptionBanner from './components/SubscriptionBanner';
import { api } from './utils/api';

function App() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('dymo-address');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [printQueue, setPrintQueue] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [printFormat, setPrintFormat] = useState('pdf');
  const [printing, setPrinting] = useState(false);

  const showToast = useCallback((message, isError = false) => {
    setToast({ message, isError });
  }, []);

  const refreshTemplates = useCallback(async () => {
    try {
      const res = await api.get('/api/templates');
      setTemplates(res.templates || []);
    } catch (e) {
      // Templates are optional — ignore failures
    }
  }, []);

  const refreshProducts = useCallback(async () => {
    const productsRes = await api.get('/api/products');
    setProducts(productsRes.products || []);
  }, []);

  // Load initial data
  useEffect(() => {
    async function loadData() {
      try {
        // 1) Make sure the server holds a valid expiring offline token.
        //    Safe on every load: the server skips the exchange when the
        //    stored token is still valid.
        try {
          await api.post('/api/auth/token-exchange', {});
        } catch (e) {
          // Standalone/mock or not embedded yet — continue anyway
        }

        // 2) Load app data
        const [productsRes, presetsRes, subRes] = await Promise.all([
          api.get('/api/products'),
          api.get('/api/labels/presets'),
          api.get('/api/subscription/status')
        ]);

        setProducts(productsRes.products || []);
        setPresets(presetsRes.presets || []);
        setSubscription(subRes.subscription);

        // 3) Load saved templates
        await refreshTemplates();

        // Handle return from Shopify billing approval
        const params = new URLSearchParams(window.location.search);
        const billingResult = params.get('billing');
        if (billingResult === 'success') {
          showToast('Subscription activated. Welcome to Pro!');
          window.history.replaceState({}, '', window.location.pathname);
        } else if (billingResult === 'error') {
          showToast('Subscription could not be activated', true);
          window.history.replaceState({}, '', window.location.pathname);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        showToast('Failed to load data', true);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [showToast, refreshTemplates]);

  const handleProductSelect = useCallback((row) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.id === row.id);
      if (exists) {
        return prev.filter((p) => p.id !== row.id);
      }
      return [...prev, row];
    });
  }, []);

  const handleAddToQueue = useCallback(
    (row, quantity = 1) => {
      setPrintQueue((prev) => {
        const exists = prev.find((p) => p.row.id === row.id);
        if (exists) {
          return prev.map((p) =>
            p.row.id === row.id ? { ...p, quantity: p.quantity + quantity } : p
          );
        }
        return [...prev, { row, quantity }];
      });
      const label = row.variantTitle ? `${row.title} (${row.variantTitle})` : row.title;
      showToast(`Added ${label} to print queue`);
    },
    [showToast]
  );

  const handleRemoveFromQueue = useCallback((rowId) => {
    setPrintQueue((prev) => prev.filter((p) => p.row.id !== rowId));
  }, []);

  const handleUpdateQuantity = useCallback((rowId, quantity) => {
    setPrintQueue((prev) =>
      prev.map((p) => (p.row.id === rowId ? { ...p, quantity } : p))
    );
  }, []);

  // Barcode generated (single or bulk) — update rows in place
  const handleBarcodeGenerated = useCallback(
    (variantId, barcode) => {
      const patch = (row) =>
        row.id === variantId ? { ...row, barcode, hasBarcode: true } : row;

      setProducts((prev) => prev.map(patch));
      setPrintQueue((prev) =>
        prev.map((item) => ({ ...item, row: patch(item.row) }))
      );
      setSelectedProducts((prev) => prev.map(patch));
      showToast('Barcode generated and saved to Shopify');
    },
    [showToast]
  );

  // Resolve the layout from the selected template (if any)
  const resolveTemplateLayout = useCallback(() => {
    if (!selectedTemplate) return null;
    const template = templates.find((t) => String(t.id) === String(selectedTemplate));
    if (!template) return null;
    try {
      return typeof template.elements === 'string'
        ? JSON.parse(template.elements)
        : template.elements;
    } catch (e) {
      return null;
    }
  }, [selectedTemplate, templates]);

  const buildLabelData = useCallback(
    (item) => ({
      productTitle: item.row.title,
      variantTitle: item.row.variantTitle || '',
      sku: item.row.sku,
      price: item.row.price,
      comparePrice: item.row.comparePrice,
      barcode: item.row.barcode
    }),
    []
  );

  const handlePrint = useCallback(async () => {
    if (printQueue.length === 0) {
      showToast('No items in print queue', true);
      return;
    }

    setPrinting(true);
    try {
      const labels = printQueue.flatMap((item) =>
        Array(item.quantity).fill(buildLabelData(item))
      );

      const totalLabels = labels.length;
      const isBulk = totalLabels > 1;
      const template = resolveTemplateLayout();

      if (isBulk && !subscription?.active) {
        showToast('Bulk printing requires a subscription ($9/month)', true);
        return;
      }

      const endpoint = isBulk ? '/api/labels/bulk' : '/api/labels/generate';
      const body = isBulk
        ? { labels, preset: selectedPreset, template, format: printFormat }
        : { labelData: labels[0], preset: selectedPreset, template, format: printFormat };

      // Use the api client so the session token is attached
      const token = await api.getAuthToken();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        let message = 'Print failed';
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // non-JSON error
        }
        throw new Error(message);
      }

      // Download the result (PDF or ZPL)
      const isZpl = printFormat === 'zpl';
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isZpl ? `labels-${Date.now()}.zpl` : `labels-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showToast(`Successfully generated ${totalLabels} label(s)`);
      setPrintQueue([]);
    } catch (error) {
      console.error('Print error:', error);
      showToast(error.message || 'Print failed', true);
    } finally {
      setPrinting(false);
    }
  }, [
    printQueue,
    selectedPreset,
    subscription,
    printFormat,
    buildLabelData,
    resolveTemplateLayout,
    showToast
  ]);

  const handlePreview = useCallback(async () => {
    if (printQueue.length === 0) {
      showToast('No items in print queue', true);
      return;
    }
    try {
      const template = resolveTemplateLayout();
      const token = await api.getAuthToken();
      const response = await fetch('/api/labels/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          labelData: buildLabelData(printQueue[0]),
          preset: selectedPreset,
          template,
          format: 'pdf'
        })
      });

      if (!response.ok) {
        let message = 'Preview failed';
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // non-JSON
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      showToast(error.message || 'Preview failed', true);
    }
  }, [printQueue, selectedPreset, buildLabelData, resolveTemplateLayout, showToast]);

  const tabs = [
    { id: 'products', content: 'Products', panelID: 'products-panel' },
    { id: 'designer', content: 'Label Designer', panelID: 'designer-panel' },
    { id: 'queue', content: `Print Queue (${printQueue.length})`, panelID: 'queue-panel' }
  ];

  if (loading) {
    return (
      <Frame>
        <Page>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
            <Spinner size="large" />
          </div>
        </Page>
      </Frame>
    );
  }

  return (
    <Frame>
      <Page
        title="ProLabel Wizard"
        subtitle="Precision barcode label printing"
        primaryAction={{
          content: 'Print Labels',
          onAction: handlePrint,
          disabled: printQueue.length === 0
        }}
      >
        {!subscription?.active && (
          <SubscriptionBanner
            onSubscribe={() => setSelectedTab(3)}
            onStatusChange={(sub) => setSubscription(sub)}
          />
        )}

        <Layout>
          <Layout.Section>
            <Card padding="0">
              <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                {selectedTab === 0 && (
                  <ProductSelector
                    products={products}
                    selectedProducts={selectedProducts}
                    onSelect={handleProductSelect}
                    onAddToQueue={handleAddToQueue}
                    presets={presets}
                    selectedPreset={selectedPreset}
                    onPresetChange={setSelectedPreset}
                    onBarcodeGenerated={handleBarcodeGenerated}
                    loading={false}
                  />
                )}

                {selectedTab === 1 && (
                  <LabelDesigner
                    presets={presets}
                    selectedPreset={selectedPreset}
                    onPresetChange={setSelectedPreset}
                    sampleProduct={products[0] || null}
                    templates={templates}
                    onTemplatesChange={refreshTemplates}
                  />
                )}

                {selectedTab === 2 && (
                  <PrintQueue
                    queue={printQueue}
                    onRemove={handleRemoveFromQueue}
                    onUpdateQuantity={handleUpdateQuantity}
                    onPrint={handlePrint}
                    onPreview={handlePreview}
                    subscription={subscription}
                    presets={presets}
                    selectedPreset={selectedPreset}
                    onPresetChange={setSelectedPreset}
                    printFormat={printFormat}
                    onFormatChange={setPrintFormat}
                    templates={templates}
                    selectedTemplate={selectedTemplate}
                    onTemplateChange={setSelectedTemplate}
                    printing={printing}
                  />
                )}
              </Tabs>
            </Card>
          </Layout.Section>
        </Layout>

        {toast && (
          <Toast
            content={toast.message}
            error={toast.isError}
            onDismiss={() => setToast(null)}
          />
        )}
      </Page>
    </Frame>
  );
}

export default App;
